import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from "@mui/icons-material";
import SaveIcon from "@mui/icons-material/Save";
import { Button, IconButton, Stack, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { isEqual } from "lodash";
import type { FC } from "react";
import { useEffect, useState } from "react";
import ImagePreview from "../../components/imagePreview/ImagePreview";
import { swapArr } from "../../misc/utils";
import { useAgents } from "../agents/useAgents";
import DictionaryAccordion from "./DictionaryAccordion";
import type { RegionFormValues } from "./EditRegionDialog";
import EditRegionDialog from "./EditRegionDialog";
import type { Region, ReorderRegionDto } from "./regionsApi";
import { sortRegions } from "./sortRegionsOptions";
import { useRegions } from "./useRegions";

interface RegionRowProps {
  readonly region: Region;
  readonly responsibleAgentName: string | null;
  readonly idx: number;
  readonly onDelete: () => Promise<void>;
  readonly onUpdate: (values: RegionFormValues) => Promise<void>;
  readonly moveRegion: (idx: number, targetIdx: number) => void;
  readonly regionsLength: number;
}

const RegionRow: FC<RegionRowProps> = ({
  region,
  responsibleAgentName,
  onUpdate,
  onDelete,
  idx,
  moveRegion,
  regionsLength,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  return (
    <TableRow data-testid="region-row">
      <TableCell>{region.id.userFriendlyId ?? "Brak"} </TableCell>
      <TableCell>{region.name}</TableCell>
      <TableCell>{region.description}</TableCell>
      <TableCell>{region.countries.join(", ")}</TableCell>
      <TableCell>{responsibleAgentName ?? "—"}</TableCell>
      <TableCell>
        <ImagePreview image={region.image} thumbnailSize={100} />
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={2}>
          <IconButton size="small" aria-label="Edytuj region" onClick={() => setDialogOpen(true)}>
            <EditIcon />
          </IconButton>
          <IconButton onClick={onDelete} aria-label="Usuń region ze słownika">
            <DeleteIcon />
          </IconButton>
          <IconButton
            aria-label="Przesuń w górę"
            size="small"
            disabled={idx === 0}
            onClick={() => moveRegion(idx, idx - 1)}
          >
            <KeyboardArrowUpIcon />
          </IconButton>
          <IconButton
            aria-label="Przesuń w dół"
            size="small"
            disabled={idx === regionsLength - 1}
            onClick={() => moveRegion(idx, idx + 1)}
          >
            <KeyboardArrowDownIcon />
          </IconButton>
        </Stack>
        {dialogOpen && (
          <EditRegionDialog open={dialogOpen} region={region} onSave={onUpdate} onClose={() => setDialogOpen(false)} />
        )}
      </TableCell>
    </TableRow>
  );
};

function getReordered(oldOrder: Region[], newOrder: Region[]): ReorderRegionDto[] {
  const idToOldOrder = new Map(oldOrder.map((r) => [r.id.id, r.order]));
  return newOrder.map((r, idx) => ({ id: r.id.id, order: idx })).filter((r) => idToOldOrder.get(r.id) != r.order);
}

const RegionsDictionary = () => {
  const { regions, isLoading, addRegion, updateRegion, deleteRegion, reorderRegions, isReorderPending } = useRegions();
  const { agents } = useAgents();
  const agentNamesById = new Map(agents?.map((agent) => [agent.id, agent.name]));
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const savedRegions = sortRegions(regions);
  const [orderedRegions, setOrderedRegions] = useState(savedRegions);

  useEffect(() => {
    setOrderedRegions(sortRegions(regions));
  }, [regions]);

  const moveRegion = (idx: number, targetIdx: number) => {
    setOrderedRegions((current) => swapArr(current, idx, targetIdx));
  };

  const hasChanges = !isEqual(
    orderedRegions.map((o) => o.id.id),
    savedRegions.map((o) => o.id.id),
  );

  return (
    <DictionaryAccordion title="Regiony" isLoading={isLoading}>
      <Stack
        direction="row"
        sx={{
          justifyContent: "flex-end",
          padding: 1,
        }}
      >
        <Button
          startIcon={<SaveIcon />}
          disabled={!hasChanges || isReorderPending}
          onClick={() => reorderRegions(getReordered(savedRegions, orderedRegions))}
        >
          Zapisz kolejność
        </Button>

        <Button startIcon={<AddIcon />} onClick={() => setAddDialogOpen(true)}>
          Dodaj region
        </Button>
      </Stack>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Id</TableCell>
            <TableCell>Nazwa</TableCell>
            <TableCell>Opis</TableCell>
            <TableCell>Kraje</TableCell>
            <TableCell>Osoba odpowiedzialna</TableCell>
            <TableCell>Zdjęcie</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {orderedRegions.map((region, idx) => (
            <RegionRow
              idx={idx}
              key={region.id.id}
              region={region}
              responsibleAgentName={
                region.responsibleAgentId ? (agentNamesById.get(region.responsibleAgentId) ?? null) : null
              }
              onUpdate={async (values) => {
                await updateRegion({ id: region.id.id, region: values });
              }}
              onDelete={async () => {
                if (confirm(`Usunąć region ${region.name}?`)) {
                  await deleteRegion(region.id.id);
                }
              }}
              moveRegion={moveRegion}
              regionsLength={regions.length}
            />
          ))}
        </TableBody>
      </Table>
      {addDialogOpen && (
        <EditRegionDialog
          open={addDialogOpen}
          onSave={async (values) => {
            await addRegion(values);
          }}
          onClose={() => setAddDialogOpen(false)}
        />
      )}
    </DictionaryAccordion>
  );
};

export default RegionsDictionary;
